import React, { useState, useEffect } from "react";
import { useAuthStore } from "../../stores/authStore";
import * as reviewService from "../../services/reviewService";
import type { Database } from "../../types/database";

type Review = Database['public']['Tables']['reviews']['Row'] & {
  user_profiles?: {
    full_name: string | null;
    email: string | null;
  };
  businesses?: {
    name: string | null;
  };
};

const ReviewManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [moderatingReview, setModeratingReview] = useState<string | null>(null);
  const [viewingReview, setViewingReview] = useState<Review | null>(null);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await reviewService.getAllReviews({ status: filter });
      if (response.success && response.data) {
        setReviews(response.data);
      } else {
        setError(response.error?.message || 'Failed to load reviews');
      }
    } catch (err: any) {
      console.error('Error loading reviews:', err);
      setError(err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, [filter]);

  const handleModerateReview = async (reviewId: string, action: 'approve' | 'reject') => {
    if (!user?.id) return;

    setModeratingReview(reviewId);
    try {
      const response = await reviewService.moderateReview(reviewId, user.id, action);
      if (response.success) {
        // Reload reviews to get updated data
        await loadReviews();
      } else {
        setError(response.error?.message || 'Failed to moderate review');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to moderate review');
    } finally {
      setModeratingReview(null);
    }
  };

  const getStatusBadge = (review: Review) => {
    if (!review.moderated) {
      return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">Pending</span>;
    }
    if (review.verified) {
      return <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Approved</span>;
    }
    return <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">Rejected</span>;
  };

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const ReviewModal = ({ review }: { review: Review }) => {
    if (!review) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Review Details</h3>
            <button
              onClick={() => setViewingReview(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6 space-y-4">
            {/* Customer Info */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Customer</h4>
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-medium">{review.user_profiles?.full_name || 'Anonymous'}</p>
                <p className="text-sm text-gray-600">{review.user_profiles?.email}</p>
              </div>
            </div>

            {/* Business Info */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Business</h4>
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-medium">{review.businesses?.name || 'Unknown Business'}</p>
              </div>
            </div>

            {/* Rating */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Rating</h4>
              <div className="flex items-center space-x-2">
                <span className="text-yellow-400 text-2xl">{renderStars(review.rating)}</span>
                <span className="text-lg font-medium">{review.rating}/5</span>
              </div>
            </div>

            {/* Comment */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Comment</h4>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-gray-800 whitespace-pre-wrap">
                  {review.comment || 'No comment provided'}
                </p>
              </div>
            </div>

            {/* Date & Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Date</h4>
                <p className="text-gray-600">
                  {review.created_at ? new Date(review.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Status</h4>
                {getStatusBadge(review)}
              </div>
            </div>
          </div>

          {/* Modal Footer - Actions */}
          {!review.moderated && (
            <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setViewingReview(null)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleModerateReview(review.id, 'reject');
                  setViewingReview(null);
                }}
                disabled={moderatingReview === review.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {moderatingReview === review.id ? 'Rejecting...' : 'Reject'}
              </button>
              <button
                onClick={() => {
                  handleModerateReview(review.id, 'approve');
                  setViewingReview(null);
                }}
                disabled={moderatingReview === review.id}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {moderatingReview === review.id ? 'Approving...' : 'Approve'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-red-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <h4 className="text-lg font-medium text-red-900 mb-2">Error</h4>
                  <p className="text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Controls */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filter</label>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as any)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">Pending Reviews</option>
                    <option value="approved">Approved Reviews</option>
                    <option value="rejected">Rejected Reviews</option>
                    <option value="all">All Reviews</option>
                  </select>
                </div>
              </div>
              <button
                onClick={loadReviews}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Reviews Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Reviews ({reviews.length})
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading reviews...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p>No reviews found matching your criteria.</p>
              </div>
            ) : (
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                        Business
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                        Rating
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                        Comment
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reviews.map((review) => (
                      <tr key={review.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {review.user_profiles?.full_name || 'Anonymous'}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {review.user_profiles?.email}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900 truncate">
                            {review.businesses?.name || 'Unknown Business'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-yellow-400 text-lg">
                            {renderStars(review.rating)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900 max-w-32 truncate">
                            {review.comment || 'No comment'}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {review.created_at ? new Date(review.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(review)}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => setViewingReview(review)}
                              className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                            >
                              View
                            </button>
                            {!review.moderated && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleModerateReview(review.id, 'approve')}
                                  disabled={moderatingReview === review.id}
                                  className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {moderatingReview === review.id ? '...' : '✓'}
                                </button>
                                <button
                                  onClick={() => handleModerateReview(review.id, 'reject')}
                                  disabled={moderatingReview === review.id}
                                  className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 disabled:opacity-50"
                                >
                                  {moderatingReview === review.id ? '...' : '✗'}
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Review Modal */}
      {viewingReview && <ReviewModal review={viewingReview} />}
    </div>
  );
};

export default ReviewManagementPage;